// projectWizard/WizardControls.jsx
//
// The shared control vocabulary for the wizard. Every input in every step
// comes from here, which is what makes the form feel like one system rather
// than the assortment of differently-sized fields the old dialog had.
//
// Design contract, applied by construction rather than by discipline:
//   * single-line inputs are exactly 48px tall
//   * one border radius (12px) everywhere
//   * label -> control -> helper text, always in that order, always spaced
//     the same, so nothing shifts as you move down a step
//   * the required marker and the character counter live in fixed positions
//     so a field with an error is the same height as one without (no layout
//     jump when validation appears)
//
// Accessibility: every control has a real <label> bound by htmlFor/id, error
// text is wired via aria-describedby and aria-invalid, and card selectors are
// real checkboxes/buttons so they are reachable and toggleable by keyboard.

import React from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Checkbox from '@mui/material/Checkbox';
import Switch from '@mui/material/Switch';
import IconButton from '@mui/material/IconButton';
import Collapse from '@mui/material/Collapse';
import Button from '@mui/material/Button';

export const BRAND = {
  primary: '#6C3BFF',
  primaryDark: '#5A2EE5',
  primarySoft: 'rgba(108,59,255,0.08)',
  border: '#e2e8f0',
  borderStrong: '#cbd5e1',
  text: '#0f172a',
  muted: '#64748b',
  danger: '#dc2626',
  surface: '#ffffff',
  canvas: '#f8fafc',
};

const CONTROL_SX = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: BRAND.surface,
    '& fieldset': { borderColor: BRAND.border },
    '&:hover fieldset': { borderColor: BRAND.borderStrong },
    '&.Mui-focused fieldset': { borderColor: BRAND.primary, borderWidth: '2px' },
  },
  '& .MuiOutlinedInput-input': { fontSize: 15 },
};

const SINGLE_LINE_SX = {
  ...CONTROL_SX,
  '& .MuiOutlinedInput-root': { ...CONTROL_SX['& .MuiOutlinedInput-root'], height: 48 },
};

/** Label + control + helper, with stable vertical rhythm. */
export const Field = ({ id, label, required, hint, error, counter, children }) => (
  <Box sx={{ mb: 2.5 }}>
    <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 0.75 }}>
      <Typography
        component="label"
        htmlFor={id}
        sx={{ fontSize: 14, fontWeight: 600, color: BRAND.text, display: 'block' }}
      >
        {label}
        {required ? (
          <Box component="span" aria-hidden="true" sx={{ color: BRAND.danger, ml: 0.5 }}>*</Box>
        ) : null}
        {required ? <Box component="span" className="sr-only" sx={{ position: 'absolute', left: -9999 }}> (required)</Box> : null}
      </Typography>
      {counter ? (
        <Typography sx={{ fontSize: 12, color: BRAND.muted, flexShrink: 0, ml: 2 }}>{counter}</Typography>
      ) : null}
    </Stack>
    {children}
    {/* Reserve the helper row so validation appearing never shifts layout. */}
    <Box sx={{ minHeight: 20, mt: 0.5 }}>
      {error ? (
        <Typography id={id ? id + '-error' : undefined} role="alert" sx={{ fontSize: 12.5, color: BRAND.danger }}>
          {error}
        </Typography>
      ) : hint ? (
        <Typography id={id ? id + '-hint' : undefined} sx={{ fontSize: 12.5, color: BRAND.muted }}>
          {hint}
        </Typography>
      ) : null}
    </Box>
  </Box>
);

/** Single-line or auto-growing multiline text. */
export const TextInput = React.memo(function TextInput({
  id, label, value, onChange, required, hint, error, placeholder, multiline, minRows = 3, maxLength, type = 'text',
}) {
  const v = value == null ? '' : value;
  return (
    <Field
      id={id}
      label={label}
      required={required}
      hint={hint}
      error={error}
      counter={maxLength ? `${String(v).length}/${maxLength}` : null}
    >
      <TextField
        id={id}
        fullWidth
        type={type}
        value={v}
        onChange={(e) => onChange(maxLength ? e.target.value.slice(0, maxLength) : e.target.value)}
        placeholder={placeholder}
        multiline={multiline}
        minRows={multiline ? minRows : undefined}
        error={!!error}
        aria-invalid={!!error}
        aria-describedby={error ? id + '-error' : hint ? id + '-hint' : undefined}
        sx={multiline ? CONTROL_SX : SINGLE_LINE_SX}
      />
    </Field>
  );
});

/** Native date input — no date-picker library is available in this project. */
export const DateInput = React.memo(function DateInput({ id, label, value, onChange, required, hint, error }) {
  return (
    <Field id={id} label={label} required={required} hint={hint} error={error}>
      <TextField
        id={id}
        fullWidth
        type="date"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        error={!!error}
        aria-invalid={!!error}
        aria-describedby={error ? id + '-error' : hint ? id + '-hint' : undefined}
        InputLabelProps={{ shrink: true }}
        sx={SINGLE_LINE_SX}
      />
    </Field>
  );
});

/** Number field with explicit steppers — clearer than a bare spinner. */
export const NumberInput = React.memo(function NumberInput({
  id, label, value, onChange, required, hint, error, min = 0, max, step = 1, suffix,
}) {
  const num = value === '' || value == null ? '' : Number(value);
  const clamp = (n) => {
    let out = n;
    if (min != null && out < min) out = min;
    if (max != null && out > max) out = max;
    return out;
  };
  const bump = (delta) => {
    const base = num === '' ? (min || 0) : num;
    onChange(String(clamp(base + delta)));
  };
  return (
    <Field id={id} label={label} required={required} hint={hint} error={error}>
      <Stack direction="row" spacing={1} alignItems="center">
        <TextField
          id={id}
          type="number"
          value={value == null ? '' : value}
          onChange={(e) => onChange(e.target.value === '' ? '' : String(clamp(Number(e.target.value))))}
          inputProps={{ min, max, step, 'aria-describedby': error ? id + '-error' : hint ? id + '-hint' : undefined }}
          error={!!error}
          aria-invalid={!!error}
          sx={{ ...SINGLE_LINE_SX, flex: 1 }}
        />
        {suffix ? <Typography sx={{ fontSize: 14, color: BRAND.muted, minWidth: 32 }}>{suffix}</Typography> : null}
        <Stack direction="row" spacing={0.5}>
          <IconButton aria-label={`Decrease ${label}`} onClick={() => bump(-step)} sx={{ border: `1px solid ${BRAND.border}`, borderRadius: '10px', width: 40, height: 40 }}>−</IconButton>
          <IconButton aria-label={`Increase ${label}`} onClick={() => bump(step)} sx={{ border: `1px solid ${BRAND.border}`, borderRadius: '10px', width: 40, height: 40 }}>+</IconButton>
        </Stack>
      </Stack>
    </Field>
  );
});

/** Searchable single select. Replaces the plain <Select> menus. */
export const SelectInput = React.memo(function SelectInput({
  id, label, value, onChange, options = [], required, hint, error, placeholder,
}) {
  return (
    <Field id={id} label={label} required={required} hint={hint} error={error}>
      <Autocomplete
        id={id}
        options={options}
        value={value || null}
        onChange={(_, next) => onChange(next || '')}
        isOptionEqualToValue={(o, v) => String(o) === String(v)}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={placeholder}
            error={!!error}
            aria-invalid={!!error}
            aria-describedby={error ? id + '-error' : hint ? id + '-hint' : undefined}
            sx={SINGLE_LINE_SX}
          />
        )}
      />
    </Field>
  );
});

/**
 * Multi-value input rendered as chips. Accepts free text (type + Enter) as
 * well as picking from suggestions, so it covers both the old tag string
 * field and the old multi-selects.
 */
export const ChipsInput = React.memo(function ChipsInput({
  id, label, value, onChange, options = [], required, hint, error, placeholder,
}) {
  const list = Array.isArray(value) ? value : [];
  return (
    <Field id={id} label={label} required={required} hint={hint} error={error} counter={list.length ? `${list.length} selected` : null}>
      <Autocomplete
        id={id}
        multiple
        freeSolo
        options={options}
        value={list}
        onChange={(_, next) => onChange(next.map((v) => String(v).trim()).filter(Boolean))}
        renderTags={(tags, getTagProps) =>
          tags.map((option, index) => {
            const { key, ...rest } = getTagProps({ index });
            return (
              <Chip
                key={key}
                label={option}
                {...rest}
                size="small"
                sx={{ borderRadius: '8px', bgcolor: BRAND.primarySoft, color: BRAND.primaryDark, fontWeight: 600 }}
              />
            );
          })
        }
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={list.length ? '' : placeholder}
            error={!!error}
            aria-invalid={!!error}
            aria-describedby={error ? id + '-error' : hint ? id + '-hint' : undefined}
            sx={{ ...CONTROL_SX, '& .MuiOutlinedInput-root': { ...CONTROL_SX['& .MuiOutlinedInput-root'], minHeight: 48 } }}
          />
        )}
      />
    </Field>
  );
});

/**
 * Card selector. Replaces checkbox lists — the brief explicitly asked for
 * cards rather than "ugly checkbox lists". Each card is a real checkbox for
 * assistive tech; the visual card is the label.
 */
export const CardSelectGroup = React.memo(function CardSelectGroup({
  label, hint, error, options = [], selected = [], onToggle, columns = { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr' },
}) {
  const isOn = (v) => (Array.isArray(selected) ? selected : []).indexOf(v) !== -1;
  return (
    <Box sx={{ mb: 2.5 }}>
      {label ? (
        <Typography sx={{ fontSize: 14, fontWeight: 600, color: BRAND.text, mb: 0.75 }}>{label}</Typography>
      ) : null}
      {hint ? <Typography sx={{ fontSize: 12.5, color: BRAND.muted, mb: 1.5 }}>{hint}</Typography> : null}
      <Box role="group" aria-label={label} sx={{ display: 'grid', gridTemplateColumns: columns, gap: 1.5 }}>
        {options.map((opt) => {
          const on = isOn(opt.value);
          return (
            <Box
              key={opt.value}
              component="label"
              htmlFor={`card-${opt.value}`}
              sx={{
                display: 'flex', alignItems: 'flex-start', gap: 1.25, p: 2, cursor: 'pointer',
                borderRadius: '14px', border: `1.5px solid ${on ? BRAND.primary : BRAND.border}`,
                bgcolor: on ? BRAND.primarySoft : BRAND.surface, transition: 'all .15s ease',
                '&:hover': { borderColor: on ? BRAND.primary : BRAND.borderStrong },
                '&:focus-within': { outline: `2px solid ${BRAND.primary}`, outlineOffset: 2 },
              }}
            >
              <Checkbox
                id={`card-${opt.value}`}
                checked={on}
                onChange={() => onToggle(opt.value)}
                sx={{ p: 0, mt: '2px', color: BRAND.borderStrong, '&.Mui-checked': { color: BRAND.primary } }}
              />
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 14.5, fontWeight: 600, color: BRAND.text }}>{opt.label}</Typography>
                {opt.hint ? (
                  <Typography sx={{ fontSize: 12.5, color: BRAND.muted, mt: 0.25 }}>{opt.hint}</Typography>
                ) : null}
              </Box>
            </Box>
          );
        })}
      </Box>
      {error ? <Typography role="alert" sx={{ fontSize: 12.5, color: BRAND.danger, mt: 1 }}>{error}</Typography> : null}
    </Box>
  );
});

/** Boolean shown as a card with a switch — used for certificates and toggles. */
export const ToggleCard = React.memo(function ToggleCard({ id, label, hint, checked, onChange }) {
  return (
    <Box
      component="label"
      htmlFor={id}
      sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, p: 2,
        borderRadius: '14px', cursor: 'pointer',
        border: `1.5px solid ${checked ? BRAND.primary : BRAND.border}`,
        bgcolor: checked ? BRAND.primarySoft : BRAND.surface, transition: 'all .15s ease',
        '&:focus-within': { outline: `2px solid ${BRAND.primary}`, outlineOffset: 2 },
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 14.5, fontWeight: 600, color: BRAND.text }}>{label}</Typography>
        {hint ? <Typography sx={{ fontSize: 12.5, color: BRAND.muted, mt: 0.25 }}>{hint}</Typography> : null}
      </Box>
      <Switch
        id={id}
        checked={!!checked}
        onChange={(e) => onChange(e.target.checked)}
        sx={{ '& .Mui-checked': { color: BRAND.primary }, '& .Mui-checked + .MuiSwitch-track': { backgroundColor: BRAND.primary } }}
      />
    </Box>
  );
});

/**
 * URL field with an upload affordance. Upload posts through whatever handler
 * the host passes in — no new endpoint is introduced here, and with no
 * handler supplied it degrades to a plain URL field.
 */
export const AssetInput = React.memo(function AssetInput({
  id, label, value, onChange, hint, error, onUpload, accept = 'image/*',
}) {
  const inputRef = React.useRef(null);
  const [busy, setBusy] = React.useState(false);
  const pick = async (file) => {
    if (!file || typeof onUpload !== 'function') return;
    setBusy(true);
    try {
      const url = await onUpload(file);
      if (url) onChange(url);
    } catch (e) {
      /* surfaced by the host's own error handling */
    } finally {
      setBusy(false);
    }
  };
  return (
    <Field id={id} label={label} hint={hint} error={error}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
        <TextField
          id={id}
          fullWidth
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://…"
          error={!!error}
          aria-invalid={!!error}
          sx={{ ...SINGLE_LINE_SX, flex: 1 }}
        />
        {typeof onUpload === 'function' ? (
          <>
            <input ref={inputRef} type="file" accept={accept} hidden onChange={(e) => pick(e.target.files && e.target.files[0])} />
            <Button
              variant="outlined"
              disabled={busy}
              onClick={() => inputRef.current && inputRef.current.click()}
              sx={{ height: 48, borderRadius: '12px', textTransform: 'none', fontWeight: 600, borderColor: BRAND.border, color: BRAND.primaryDark, whiteSpace: 'nowrap', px: 2.5 }}
            >
              {busy ? 'Uploading…' : 'Upload'}
            </Button>
          </>
        ) : null}
      </Stack>
      {value ? (
        <Typography sx={{ fontSize: 12, color: BRAND.muted, mt: 0.75, wordBreak: 'break-all' }}>{value}</Typography>
      ) : null}
    </Field>
  );
});

/** The card every step section sits in. */
export const SectionCard = ({ title, description, children, sx }) => (
  <Paper
    elevation={0}
    sx={{
      p: 3, borderRadius: '18px', border: `1px solid ${BRAND.border}`,
      boxShadow: '0 1px 2px rgba(16,24,40,.04), 0 8px 24px -12px rgba(16,24,40,.10)',
      bgcolor: BRAND.surface, ...sx,
    }}
  >
    {title ? (
      <Box sx={{ mb: description ? 0.5 : 2.5 }}>
        <Typography sx={{ fontSize: 17, fontWeight: 700, color: BRAND.text }}>{title}</Typography>
      </Box>
    ) : null}
    {description ? (
      <Typography sx={{ fontSize: 13.5, color: BRAND.muted, mb: 2.5 }}>{description}</Typography>
    ) : null}
    {children}
  </Paper>
);

/** Collapsible home for pre-existing fields the redesign did not name. */
export const AdvancedPanel = ({ children, count }) => {
  const [open, setOpen] = React.useState(false);
  return (
    <Box sx={{ mt: 1, pt: 2, borderTop: `1px dashed ${BRAND.border}` }}>
      <Button
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        sx={{ textTransform: 'none', fontWeight: 600, color: BRAND.muted, px: 1, '&:hover': { bgcolor: 'transparent', color: BRAND.primaryDark } }}
      >
        {open ? '▾' : '▸'}&nbsp;Advanced options{count ? ` (${count})` : ''}
      </Button>
      <Collapse in={open} unmountOnExit>
        <Box sx={{ pt: 2 }}>{children}</Box>
      </Collapse>
    </Box>
  );
};

export default {
  Field, TextInput, DateInput, NumberInput, SelectInput, ChipsInput,
  CardSelectGroup, ToggleCard, AssetInput, SectionCard, AdvancedPanel, BRAND,
};
